import React, { useEffect, useRef, useCallback } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minHeight?: number;
}

/**
 * WYSIWYG editor built on Quill.
 *
 * Renders with the "wiki-editor" class so styles/wiki.css applies the
 * minimal toolbar, article typography and sticky toolbar.
 */
export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start writing…',
  disabled = false,
  className = '',
  minHeight = 500,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const onChangeRef = useRef(onChange);

  // Keep the callback ref in sync without re-mounting Quill
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Mount Quill once
  useEffect(() => {
    if (!containerRef.current) return;

    // Build DOM structure: toolbar target + editor target
    containerRef.current.innerHTML = '';
    const editorEl = document.createElement('div');
    containerRef.current.appendChild(editorEl);

    const quill = new Quill(editorEl, {
      theme: 'snow',
      placeholder,
      readOnly: disabled,
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          ['link', 'image'],
          [{ align: [] }],
          ['clean'],
        ],
      },
    });

    quillRef.current = quill;

    // Set initial content
    if (value) {
      const delta = quill.clipboard.convert({ html: value });
      quill.setContents(delta, 'silent');
    }

    // Fire onChange with HTML output
    const handleChange = () => {
      const html = editorEl.querySelector('.ql-editor')?.innerHTML || '';
      // Quill emits "<p><br></p>" for empty editor – normalise to ""
      const cleaned = html === '<p><br></p>' ? '' : html;
      onChangeRef.current(cleaned);
    };
    quill.on('text-change', handleChange);

    // Apply min-height to editor area
    const qlEditor = editorEl.querySelector('.ql-editor') as HTMLElement;
    if (qlEditor) {
      qlEditor.style.minHeight = `${minHeight}px`;
    }

    // On phones the toolbar is one horizontally scrolling row, which would clip the
    // heading and alignment menus; pin open menus to the viewport instead.
    const toolbarEl = containerRef.current.querySelector('.ql-toolbar') as HTMLElement | null;
    const phone = window.matchMedia('(max-width: 639px)');
    const placePickerMenus = () => {
      toolbarEl?.querySelectorAll<HTMLElement>('.ql-picker').forEach((picker) => {
        const options = picker.querySelector<HTMLElement>('.ql-picker-options');
        if (!options) return;
        if (phone.matches && picker.classList.contains('ql-expanded')) {
          const rect = picker.getBoundingClientRect();
          options.style.position = 'fixed';
          options.style.top = `${rect.bottom + 6}px`;
          options.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - options.offsetWidth - 8))}px`;
          options.style.marginTop = '0';
        } else if (options.style.position) {
          options.style.position = '';
          options.style.top = '';
          options.style.left = '';
          options.style.marginTop = '';
        }
      });
    };
    const pickerObserver = new MutationObserver(placePickerMenus);
    if (toolbarEl) {
      pickerObserver.observe(toolbarEl, { attributes: true, attributeFilter: ['class'], subtree: true });
    }

    return () => {
      pickerObserver.disconnect();
      quill.off('text-change', handleChange);
      quillRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes (e.g. when loading existing content)
  const syncExternalValue = useCallback((html: string) => {
    const quill = quillRef.current;
    if (!quill) return;
    const current = (quill.root as HTMLElement).innerHTML;
    const normalised = current === '<p><br></p>' ? '' : current;
    if (html !== normalised) {
      const delta = quill.clipboard.convert({ html: html || '' });
      quill.setContents(delta, 'silent');
    }
  }, []);

  useEffect(() => {
    syncExternalValue(value);
  }, [value, syncExternalValue]);

  // Toggle read-only when disabled prop changes
  useEffect(() => {
    quillRef.current?.enable(!disabled);
  }, [disabled]);

  return (
    <div
      className={`wiki-editor rounded-2xl border border-neutral-200 bg-white font-ui ${className}`}
      ref={containerRef}
    />
  );
};
